// Learning Tracks query layer. Pairs with `lib/mutations/learning-tracks.ts`.
//
// Tracks are curated, ordered lists of existing resources. The list endpoint
// already gates on the server: admins see drafts, members see published
// tracks, and a signed-out visitor sees only published tracks holding an item
// they can open. The detail endpoint returns ordered items (for a signed-out
// visitor, only the ones they can open), each carrying an `availability`
// verdict for this viewer and, exactly when that verdict is `available`, the
// canonical `ResourceSummary` (the same DTO the resources library renders).
// `fetchLearningTrack` parses each item into a discriminated union so the
// pairing is enforced by the type.

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  type QueryClient,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { PageMetaLoaderData } from '../analytics/page-meta';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import { editorialTitleSubject } from '../seo-text';
import { loadCurrentForRoute } from './load-for-route';
import { QUERY_KEY_REGISTRY } from './normalize';
import { parseResourceSummary } from './resources';

// ---- Public types ----

export type LearningTrackSummary =
  components['schemas']['LearningTrackSummary'];
type LearningTrackItemWire = components['schemas']['LearningTrackItem'];
type LearningTrackDetailWire = components['schemas']['LearningTrackDetail'];
type TrackResourceSummary = components['schemas']['ResourceSummary'];

export type LearningTrackItemAvailability = LearningTrackItemWire['availability'];

// `unavailable`: unpublished, held by moderation, or gated to a Network the
// viewer is not in. Signed-in viewers only: a signed-out viewer is sent only
// the items they can open.
export type LearningTrackItem = Omit<
  LearningTrackItemWire,
  'availability' | 'resource'
> &
  (
    | { readonly availability: 'available'; readonly resource: TrackResourceSummary }
    | {
        readonly availability: Exclude<LearningTrackItemAvailability, 'available'>;
        readonly resource: null;
      }
  );

export type LearningTrackDetail = Omit<LearningTrackDetailWire, 'items'> & {
  readonly items: readonly LearningTrackItem[];
};
export type LearningTrackCursorPage =
  components['schemas']['LearningTrackCursorPage'];
export type LearningTrackTopic = components['schemas']['LearningTrackTopic'];

export type LearningTracksListParams = {
  readonly q?: string;
  /** Topic slugs — only tracks containing a resource tagged with any of them. */
  readonly tags?: ReadonlyArray<string>;
  readonly pageSize?: number;
};

// ---- Query keys + leaf factories ----
//
// `all`/`lists` are invalidation prefixes (bare arrays). `list`/`detail` are
// leaves returning `infiniteQueryOptions`/`queryOptions` — key built adjacent
// to the `queryFn` from one normalized `params` object (decision 3).
//
// `list` is the browse view (published tracks, admins included); `adminList`
// is the admin library, drafts included. Both sit under `lists()` so every
// track mutation's invalidation and optimistic reorder reaches them.

export const learningTracksKeys = {
  all: ['learning-tracks'] as const,
  lists: () => [...learningTracksKeys.all, 'list'] as const,

  list: (params: LearningTracksListParams) =>
    infiniteQueryOptions({
      queryKey: [...learningTracksKeys.lists(), params] as const,
      queryFn: ({ pageParam }) =>
        fetchLearningTracks('/api/v1/learning-tracks', {
          ...params,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    }),

  adminList: (params: LearningTracksListParams) =>
    infiniteQueryOptions({
      queryKey: [...learningTracksKeys.lists(), 'admin', params] as const,
      queryFn: ({ pageParam }) =>
        fetchLearningTracks('/api/v1/admin/learning-tracks', {
          ...params,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: [...learningTracksKeys.all, 'detail', id] as const,
      queryFn: () => fetchLearningTrack(id),
    }),

  topics: () =>
    queryOptions({
      queryKey: [...learningTracksKeys.all, 'topics'] as const,
      queryFn: fetchLearningTrackTopics,
      staleTime: 5 * 60 * 1000,
    }),
};

// ---- Fetchers ----

export async function fetchLearningTracks(
  path: '/api/v1/learning-tracks' | '/api/v1/admin/learning-tracks',
  params: LearningTracksListParams & { cursor?: string },
): Promise<LearningTrackCursorPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET(path, {
    params: {
      query: {
        ...(params.q !== undefined && params.q.length > 0
          ? { q: params.q }
          : {}),
        ...(params.tags !== undefined && params.tags.length > 0
          ? { tag: params.tags.join(',') }
          : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        ...(params.pageSize !== undefined
          ? { pageSize: params.pageSize }
          : {}),
      },
    },
  }));
  return data;
}

// The Learning Tracks tab's topic directory: content topics carried by at
// least one track the viewer can browse.
export async function fetchLearningTrackTopics(): Promise<LearningTrackTopic[]> {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/learning-tracks/topics'));
}

export async function fetchLearningTrack(
  id: string,
): Promise<LearningTrackDetail> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/learning-tracks/{id}',
    { params: { path: { id } } },
  ));
  return { ...data, items: data.items.map(parseLearningTrackItem) };
}

// The server contract: `resource` is non-null exactly when `availability` is
// `available`. A response breaking that is a server bug, surfaced as a load
// failure rather than rendered as a half-populated row.
function parseLearningTrackItem(item: LearningTrackItemWire): LearningTrackItem {
  const { availability, resource, ...rest } = item;
  if (availability === 'available' && resource !== null) {
    return { ...rest, availability, resource: parseResourceSummary(resource) };
  }
  if (availability !== 'available' && resource === null) {
    return { ...rest, availability, resource };
  }
  throw new Error(
    `LEARNING_TRACK_ITEM_CONTRACT: item ${item.resourceId} has availability "${availability}" with resource ${resource === null ? 'null' : 'present'}`,
  );
}

// ---- Hooks ----

export function useInfiniteLearningTracks(
  params: LearningTracksListParams,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({ ...learningTracksKeys.list(params), ...options });
}

export function useInfiniteAdminLearningTracks(params: LearningTracksListParams) {
  return useInfiniteQuery(learningTracksKeys.adminList(params));
}

export function useLearningTrackTopics(options?: { enabled?: boolean }) {
  return useQuery({ ...learningTracksKeys.topics(), ...options });
}

export function useLearningTrack(
  id: string,
  options?: { enabled?: boolean; retry?: boolean | number; retryOnMount?: boolean },
) {
  return useQuery({ ...learningTracksKeys.detail(id), ...options });
}

// The public track page's loader data.
export async function loadLearningTrackMeta(
  queryClient: QueryClient,
  id: string,
): Promise<PageMetaLoaderData> {
  const track = await loadCurrentForRoute(queryClient, learningTracksKeys.detail(id));
  return track === undefined ? {} : { pageTitle: editorialTitleSubject(track) };
}

// The admin track page's loader data, from the entry the public page reads.
// Read without retries, as the admin view reads it, so a failure shows at once
// instead of after the backoff; no page_id, which only entity pages report.
export async function loadAdminLearningTrackMeta(
  queryClient: QueryClient,
  id: string,
): Promise<PageMetaLoaderData> {
  const track = await loadCurrentForRoute(queryClient, {
    ...learningTracksKeys.detail(id),
    retry: false,
  });
  return track === undefined ? {} : { pageTitle: editorialTitleSubject(track) };
}

// ---- Resource picker (uses the resources/search endpoint the rest of the
//      product picks resources through). Shaped to match the picker shape
//      `TypeaheadMultiSelect` consumes. ----

export type LearningTrackResourcePickerItem = {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly publishedAt: string | null;
};

// Curation pickers (learning-track admin, topic curation) attach DRAFTS
// too, so this searches the admin listing — the member-facing /resources
// endpoints compose `readable`, which hides unpublished work. Both
// consumers are admin-only surfaces; a non-admin caller would get 403.
export async function searchResourcesForPicker(
  q: string,
): Promise<readonly LearningTrackResourcePickerItem[]> {
  const trimmed = q.trim();
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/resources', {
    params: {
      query: {
        pageSize: 20,
        // Below 2 chars the server applies no text filter — an unfiltered
        // recency page, which is what an just-opened picker should show.
        ...(trimmed.length > 0 ? { q: trimmed } : {}),
      },
    },
  }));
  return data.items.map((r) => ({
    id: r.id,
    title: r.title,
    kind: r.kind,
    publishedAt: r.publishedAt,
  }));
}

// ---- Query-key registry (decision 6 invariant test) ----

QUERY_KEY_REGISTRY.push(
  {
    name: 'learningTracksKeys.list',
    sampleKey: learningTracksKeys.list({ q: 'leadership', pageSize: 20 })
      .queryKey,
  },
  {
    name: 'learningTracksKeys.adminList',
    sampleKey: learningTracksKeys.adminList({ pageSize: 100 }).queryKey,
  },
  {
    name: 'learningTracksKeys.detail',
    sampleKey: learningTracksKeys.detail('track-1').queryKey,
  },
  {
    name: 'learningTracksKeys.topics',
    sampleKey: learningTracksKeys.topics().queryKey,
  },
);
